import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, TextField, Button, Typography,
  CircularProgress, IconButton, InputAdornment, Stepper, Step, StepLabel,
  Divider, Alert, Chip
} from '@mui/material';
import {
  Visibility, VisibilityOff, Home as HomeIcon, Person as PersonIcon,
  Lock as LockIcon, ChildCare as ChildIcon, Add as AddIcon, Delete as DeleteIcon,
  Check as CheckIcon, VpnKey as InviteIcon, PlayArrow as DemoIcon
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useThemeMode } from '../../contexts/ThemeContext';
import api from '../../services/api';
import { CATEGORY_PRESETS, CATEGORY_LABELS } from '../../utils/categories';

const PINK = {
  main: '#e91e8c',
  light: '#f06292',
  dark: '#c2185b',
  hot: '#ff1493',
  bg: '#fce4ec',
  darkBg: '#2d1a24',
  gradient: 'linear-gradient(135deg, #e91e8c 0%, #f06292 50%, #ff1493 100%)',
  glow: '0 0 24px #e91e8c44, 0 8px 32px #e91e8c22',
  border: '#e91e8c33',
};

const SetupWizard = ({ onComplete }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [inviteCodeRequired, setInviteCodeRequired] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  const [inviteValidated, setInviteValidated] = useState(false);
  const [demoUrl, setDemoUrl] = useState(null);

  const [parentName, setParentName] = useState('');
  const [parentUsername, setParentUsername] = useState('');
  const [parentPassword, setParentPassword] = useState('');
  const [showParentPassword, setShowParentPassword] = useState(false);

  const [familyPassword, setFamilyPassword] = useState('');
  const [familyPasswordConfirm, setFamilyPasswordConfirm] = useState('');
  const [showFamilyPassword, setShowFamilyPassword] = useState(false);

  const [kioskPassword, setKioskPassword] = useState('');
  const [showKioskPassword, setShowKioskPassword] = useState(false);

  const [children, setChildren] = useState([{ name: '', username: '', password: '', pin: '', category: '' }]);

  const { isDark } = useThemeMode();

  const STEPS = inviteCodeRequired && !inviteValidated
    ? ['Kutsukoodi', 'Vanhempi', 'Perheen salasana', 'Kioski', 'Käyttäjät']
    : ['Vanhempi', 'Perheen salasana', 'Kioski', 'Käyttäjät'];

  useEffect(() => {
    const checkInviteRequired = async () => {
      try {
        const response = await api.getSetupStatus();
        setInviteCodeRequired(response.data?.inviteCodeRequired || false);
        setDemoUrl(response.data?.demoUrl || null);
      } catch (err) {
        // Assume no invite required on error
      } finally {
        setLoading(false);
      }
    };
    checkInviteRequired();
  }, []);

  const handleNext = async () => {
    setError('');

    if (inviteCodeRequired && !inviteValidated && activeStep === 0) {
      if (!inviteCode.trim()) { setError('Kutsukoodi vaaditaan'); return; }
      setLoading(true);
      try {
        await api.validateInviteCode(inviteCode.trim());
        setInviteValidated(true);
        setActiveStep(0);
      } catch (err) {
        setError(err.response?.data?.error || 'Virheellinen kutsukoodi');
      } finally {
        setLoading(false);
      }
      return;
    }

    if (activeStep === 0) {
      if (!parentName.trim()) { setError('Nimi vaaditaan'); return; }
      if (!parentUsername.trim()) { setError('Käyttäjätunnus vaaditaan'); return; }
      if (!parentPassword || parentPassword.length < 6) { setError('Salasanan tulee olla vähintään 6 merkkiä'); return; }
    }

    if (activeStep === 1) {
      if (!familyPassword || familyPassword.length < 6) { setError('Perheen salasanan tulee olla vähintään 6 merkkiä'); return; }
      if (familyPassword !== familyPasswordConfirm) { setError('Salasanat eivät täsmää'); return; }
    }

    if (activeStep === STEPS.length - 1) {
      handleSubmit();
      return;
    }

    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setError('');
    setActiveStep(prev => prev - 1);
  };

  const addChild = () => {
    setChildren([...children, { name: '', username: '', password: '', pin: '', category: '' }]);
  };

  const removeChild = (index) => {
    setChildren(children.filter((_, i) => i !== index));
  };

  const updateChild = (index, field, value) => {
    const updated = [...children];
    updated[index][field] = value;
    setChildren(updated);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      const validChildren = children.filter(c => c.name.trim() && c.username.trim());

      const response = await api.completeSetup({
        inviteCode: inviteCode || undefined,
        parentName: parentName.trim(),
        parentUsername: parentUsername.trim(),
        parentPassword,
        familyPassword,
        kioskPassword: kioskPassword || undefined,
        children: validChildren.map(c => ({
          name: c.name.trim(),
          username: c.username.trim(),
          password: c.password || undefined,
          pin: c.pin || undefined,
          category: c.category || undefined
        }))
      });

      localStorage.setItem('auth_token', response.data.token);
      localStorage.setItem('family_device_token', response.data.deviceToken);
      api.setAuthToken(response.data.token);

      onComplete?.(response.data.user);
    } catch (err) {
      setError(err.response?.data?.error || 'Asennus epäonnistui');
      setLoading(false);
    }
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      bgcolor: isDark ? 'background.paper' : '#fff',
      '& fieldset': { borderColor: isDark ? '#444' : '#ddd' },
      '&:hover fieldset': { borderColor: PINK.light },
      '&.Mui-focused fieldset': { borderColor: PINK.main },
    },
    '& .MuiInputLabel-root': { color: isDark ? '#aaa' : '#666' },
    '& .MuiInputLabel-root.Mui-focused': { color: PINK.main },
    '& .MuiInputBase-input': {
      color: isDark ? '#fff' : '#1a1a1a',
      '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus': {
        WebkitBoxShadow: `0 0 0 100px ${isDark ? '#2a2a3a' : '#fff'} inset`,
        WebkitTextFillColor: isDark ? '#fff' : '#1a1a1a',
        caretColor: isDark ? '#fff' : '#1a1a1a',
      },
    },
    '& .MuiFormHelperText-root': { color: isDark ? '#999' : '#888' },
  };

  const renderStepContent = () => {
    if (inviteCodeRequired && !inviteValidated && activeStep === 0) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <InviteIcon sx={{ fontSize: 48, color: PINK.main, mb: 1 }} />
            <Typography variant="h6" fontWeight={700}>Kutsukoodi</Typography>
            <Typography variant="body2" sx={{ color: isDark ? '#aaa' : '#666' }}>
              Syötä saamasi kutsukoodi jatkaaksesi asennusta
            </Typography>
          </Box>
          <TextField
            fullWidth
            label="Kutsukoodi"
            value={inviteCode}
            onChange={(e) => setInviteCode(e.target.value)}
            autoFocus
            disabled={loading}
            sx={inputSx}
          />
        </Box>
      );
    }

    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <PersonIcon sx={{ fontSize: 48, color: PINK.main, mb: 1 }} />
              <Typography variant="h6" fontWeight={700}>Luo vanhemman tili</Typography>
              <Typography variant="body2" sx={{ color: isDark ? '#aaa' : '#666' }}>
                Tämä on pääkäyttäjätili sovelluksen hallintaan
              </Typography>
            </Box>
            <TextField fullWidth label="Nimi" value={parentName} onChange={(e) => setParentName(e.target.value)} autoFocus sx={inputSx} />
            <TextField fullWidth label="Käyttäjätunnus" value={parentUsername} onChange={(e) => setParentUsername(e.target.value)} sx={inputSx} />
            <TextField
              fullWidth
              label="Salasana"
              type={showParentPassword ? 'text' : 'password'}
              value={parentPassword}
              onChange={(e) => setParentPassword(e.target.value)}
              helperText="Vähintään 6 merkkiä"
              sx={inputSx}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowParentPassword(!showParentPassword)} edge="end" aria-label={showParentPassword ? 'Piilota salasana' : 'Näytä salasana'}>
                      {showParentPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Box>
        );

      case 1:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <LockIcon sx={{ fontSize: 48, color: PINK.main, mb: 1 }} />
              <Typography variant="h6" fontWeight={700}>Perheen salasana</Typography>
              <Typography variant="body2" sx={{ color: isDark ? '#aaa' : '#666' }}>
                Tämä salasana vaaditaan kun uusi laite liitetään perheeseen
              </Typography>
            </Box>
            <TextField
              fullWidth
              label="Perheen salasana"
              type={showFamilyPassword ? 'text' : 'password'}
              value={familyPassword}
              onChange={(e) => setFamilyPassword(e.target.value)}
              helperText="Vähintään 6 merkkiä"
              autoFocus
              sx={inputSx}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowFamilyPassword(!showFamilyPassword)} edge="end" aria-label={showFamilyPassword ? 'Piilota salasana' : 'Näytä salasana'}>
                      {showFamilyPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <TextField
              fullWidth
              label="Vahvista salasana"
              type={showFamilyPassword ? 'text' : 'password'}
              value={familyPasswordConfirm}
              onChange={(e) => setFamilyPasswordConfirm(e.target.value)}
              sx={inputSx}
            />
          </Box>
        );

      case 2:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <HomeIcon sx={{ fontSize: 48, color: PINK.main, mb: 1 }} />
              <Typography variant="h6" fontWeight={700}>Kioskitili (valinnainen)</Typography>
              <Typography variant="body2" sx={{ color: isDark ? '#aaa' : '#666' }}>
                Kioskitili on tablettia varten. Se näyttää julkiset sivut ja käyttäjät kirjautuvat PIN-koodilla.
              </Typography>
            </Box>
            <TextField
              fullWidth
              label="Kioskin salasana"
              type={showKioskPassword ? 'text' : 'password'}
              value={kioskPassword}
              onChange={(e) => setKioskPassword(e.target.value)}
              helperText="Jätä tyhjäksi jos et tarvitse kioskitiliä"
              autoFocus
              sx={inputSx}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowKioskPassword(!showKioskPassword)} edge="end" aria-label={showKioskPassword ? 'Piilota salasana' : 'Näytä salasana'}>
                      {showKioskPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Box>
        );

      case 3:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <ChildIcon sx={{ fontSize: 48, color: PINK.main, mb: 1 }} />
              <Typography variant="h6" fontWeight={700}>Lisää käyttäjät</Typography>
              <Typography variant="body2" sx={{ color: isDark ? '#aaa' : '#666' }}>
                Voit lisätä käyttäjiä myöhemminkin asetuksista
              </Typography>
            </Box>

            {children.map((child, index) => (
              <Card key={index} variant="outlined" sx={{ p: 2, borderColor: `${PINK.main}22` }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700}>Käyttäjä {index + 1}</Typography>
                  {children.length > 1 && (
                    <IconButton size="small" onClick={() => removeChild(index)} aria-label="Poista käyttäjä">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <TextField size="small" fullWidth label="Nimi *" value={child.name} onChange={(e) => updateChild(index, 'name', e.target.value)} sx={inputSx} />
                  <TextField size="small" fullWidth label="Käyttäjätunnus *" value={child.username} onChange={(e) => updateChild(index, 'username', e.target.value)} sx={inputSx} />
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {CATEGORY_PRESETS.map(cat => (
                      <Chip
                        key={cat}
                        label={CATEGORY_LABELS[cat] || cat}
                        size="small"
                        onClick={() => updateChild(index, 'category', cat)}
                        variant={child.category === cat ? 'filled' : 'outlined'}
                        color={child.category === cat ? 'primary' : 'default'}
                        sx={{ cursor: 'pointer', fontSize: '0.7rem' }}
                      />
                    ))}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField size="small" fullWidth label="Salasana" type="password" value={child.password} onChange={(e) => updateChild(index, 'password', e.target.value)} helperText="Valinnainen" sx={inputSx} />
                    <TextField
                      size="small"
                      sx={{ width: 120, ...inputSx }}
                      label="PIN"
                      value={child.pin}
                      onChange={(e) => updateChild(index, 'pin', e.target.value.replace(/\D/g, '').slice(0, 4))}
                      inputProps={{ maxLength: 4, inputMode: 'numeric' }}
                      helperText="4 numeroa"
                    />
                  </Box>
                </Box>
              </Card>
            ))}

            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addChild}
              sx={{ alignSelf: 'flex-start', borderColor: PINK.main, color: PINK.main, '&:hover': { borderColor: PINK.hot, bgcolor: `${PINK.main}10` } }}
            >
              Lisää käyttäjä
            </Button>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh', display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      bgcolor: isDark ? PINK.darkBg : PINK.bg, p: 2, pt: { xs: 4, sm: 8 }, position: 'relative', overflow: 'hidden',
    }}>
      {/* Ambient orbs */}
      <Box sx={{
        position: 'absolute', top: '-20%', right: '-10%',
        width: 400, height: 400, borderRadius: '50%',
        background: `radial-gradient(circle, ${PINK.main}15 0%, transparent 70%)`,
        pointerEvents: 'none',
      }} />
      <Box sx={{
        position: 'absolute', bottom: '-15%', left: '-10%',
        width: 350, height: 350, borderRadius: '50%',
        background: `radial-gradient(circle, ${PINK.light}12 0%, transparent 70%)`,
        pointerEvents: 'none',
      }} />

      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        style={{ width: '100%', maxWidth: 500, position: 'relative', zIndex: 1 }}
      >
        <Card sx={{
          borderRadius: 2,
          boxShadow: isDark
            ? `0 8px 32px rgba(0,0,0,0.5), 0 0 0 1px ${PINK.border}`
            : `0 8px 32px ${PINK.main}12, 0 0 0 1px ${PINK.border}`,
          border: `1px solid ${PINK.main}22`,
          bgcolor: isDark ? 'background.default' : '#fff',
        }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <Box sx={{
                  width: 64, height: 64, borderRadius: '50%',
                  background: PINK.gradient,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  mx: 'auto', mb: 1.5, boxShadow: PINK.glow, overflow: 'hidden',
                }}>
                  <Box component="img" src="/icons/icon-96x96.png" alt=""
                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </Box>
              </motion.div>
              <Typography variant="h5" fontWeight={900} sx={{ color: isDark ? PINK.light : PINK.dark }}>
                Perheen kotiaskareet
              </Typography>
              <Typography variant="body2" sx={{ color: isDark ? '#aaa' : '#666' }}>
                Tervetuloa! Aloitetaan asennus.
              </Typography>
              {demoUrl && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DemoIcon />}
                  {...(demoUrl === '__internal__'
                    ? { onClick: () => window.location.reload() }
                    : { href: demoUrl, target: '_blank', rel: 'noopener noreferrer' }
                  )}
                  sx={{ mt: 2, borderColor: PINK.main, color: PINK.main, '&:hover': { borderColor: PINK.hot, bgcolor: `${PINK.main}10` } }}
                >
                  Kokeile demoa ensin
                </Button>
              )}
            </Box>

            <Stepper activeStep={activeStep} alternativeLabel sx={{
              mb: 3,
              '& .MuiStepIcon-root.Mui-active': { color: PINK.main },
              '& .MuiStepIcon-root.Mui-completed': { color: PINK.main },
            }}>
              {STEPS.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            <Divider sx={{ mb: 3 }} />

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
            )}

            <AnimatePresence mode="wait">
              <motion.div
                key={activeStep}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                {renderStepContent()}
              </motion.div>
            </AnimatePresence>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button
                disabled={activeStep === 0 || loading}
                onClick={handleBack}
                sx={{ color: PINK.main }}
              >
                Takaisin
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={loading}
                startIcon={activeStep === STEPS.length - 1 ? (loading ? <CircularProgress size={20} color="inherit" /> : <CheckIcon />) : null}
                sx={{
                  fontWeight: 700, borderRadius: 2,
                  background: PINK.gradient, color: '#fff',
                  boxShadow: `0 4px 16px ${PINK.main}44`,
                  '&:hover': { boxShadow: `0 6px 20px ${PINK.main}66` },
                  '&.Mui-disabled': { background: '#eee', color: '#aaa' },
                }}
              >
                {activeStep === STEPS.length - 1 ? (loading ? 'Asennetaan...' : 'Valmis') : 'Seuraava'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </motion.div>
    </Box>
  );
};

export default SetupWizard;
